import { Pair } from '@uniswap/v2-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import IUniswapV2PairABI from '@uniswap/v2-core/build/IUniswapV2Pair.json'
import { Interface } from '@ethersproject/abi'
import { useActiveWeb3React } from './index'
import { useMultipleContractSingleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { Currency, TokenAmount } from '@uniswap/sdk-core'
import { CURRENT_CHAIN, V2_FACTORY_ADDRESS } from '@hyperswap/addresses'
import { useContract } from './useContract'
import IUniswapV2FactoryABI from '@uniswap/v2-core/build/IUniswapV2Factory.json'
import { ethers } from 'ethers'

const PAIR_INTERFACE = new Interface(IUniswapV2PairABI.abi)

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export function useV2Pairs(currencies: [Currency | undefined, Currency | undefined][]): [PairState, Pair | null][] {
  const { chainId } = useActiveWeb3React()

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId),
      ]),
    [chainId, currencies]
  )

  const pairAddresses = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) => {
        return tokenA &&
          tokenB &&
          !tokenA.equals(tokenB) &&
          tokenA.chainId === CURRENT_CHAIN &&
          tokenB.chainId === CURRENT_CHAIN
          ? Pair.getAddress(tokenA, tokenB)
          : undefined
      }),
    [tokens]
  )

  const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')

  return useMemo(() => {
    return results.map((result, i) => {
      const { result: reserves, loading } = result
      const tokenA = tokens[i][0]
      const tokenB = tokens[i][1]

      if (loading) return [PairState.LOADING, null]
      if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
      if (!reserves) return [PairState.NOT_EXISTS, null]
      const { reserve0, reserve1 } = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
      return [
        PairState.EXISTS,
        new Pair(new TokenAmount(token0, reserve0.toString()), new TokenAmount(token1, reserve1.toString())),
      ]
    })
  }, [results, tokens])
}

export function useV2Pair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  return useV2Pairs([[tokenA, tokenB]])[0]
}

export function useV2FactoryPairExists(tokenA?: string, tokenB?: string): PairState {
  const [pairState, setPairState] = useState<PairState>(PairState.LOADING)
  const factoryContract = useContract(V2_FACTORY_ADDRESS, IUniswapV2FactoryABI.abi)

  const checkPairExists = useCallback(async () => {
    if (!tokenA || !tokenB || !factoryContract) {
      setPairState(PairState.INVALID)
      return
    }

    try {
      const result = await factoryContract.getPair(tokenA, tokenB)
      setPairState(result === ethers.constants.AddressZero ? PairState.NOT_EXISTS : PairState.EXISTS)
    } catch (error) {
      console.error('Error checking pair existence:', error)
      setPairState(PairState.INVALID)
    }
  }, [tokenA, tokenB, factoryContract])

  useEffect(() => {
    checkPairExists()
  }, [checkPairExists])

  return pairState
}
